import { createSlice } from '@reduxjs/toolkit';
import appDataManager from '../../services/AppDataManager';

const initialState = {
  drivers: [],
  vehicles: [],
  rides: [],
  shifts: { drivers: {} },
  customers: [],
  activities: [], // Konsolidierte Activities für WaitTimeManager
  isLoading: {
    drivers: false,
    vehicles: false,
    rides: false,
    shifts: false,
    customers: false,
    activities: false
  },
  error: {
    drivers: null,
    vehicles: null,
    rides: null,
    shifts: null,
    customers: null,
    activities: null
  },
  isManagerActive: false,
  lastUpdated: {
    drivers: null,
    vehicles: null,
    rides: null,
    shifts: null,
    customers: null,
    activities: null
  }
};

const appDataSlice = createSlice({
  name: 'appData',
  initialState,
  reducers: {
    setDrivers: (state, action) => {
      state.drivers = action.payload;
      state.lastUpdated.drivers = new Date().toISOString();
      state.isLoading.drivers = false;
      state.error.drivers = null;
    },
    setVehicles: (state, action) => {
      state.vehicles = action.payload;
      state.lastUpdated.vehicles = new Date().toISOString();
      state.isLoading.vehicles = false;
      state.error.vehicles = null;
    },
    setRides: (state, action) => {
      state.rides = action.payload;
      state.lastUpdated.rides = new Date().toISOString();
      state.isLoading.rides = false;
      state.error.rides = null;
    },
    setShifts: (state, action) => {
      state.shifts = action.payload;
      state.lastUpdated.shifts = new Date().toISOString();
      state.isLoading.shifts = false;
      state.error.shifts = null;
    },
    setCustomers: (state, action) => {
      state.customers = action.payload;
      state.lastUpdated.customers = new Date().toISOString();
      state.isLoading.customers = false;
      state.error.customers = null;
    },
    setActivities: (state, action) => {
      state.activities = action.payload;
      state.lastUpdated.activities = new Date().toISOString();
      state.isLoading.activities = false;
      state.error.activities = null;
    },
    setLoading: (state, action) => {
      const { dataType, isLoading } = action.payload;
      state.isLoading[dataType] = isLoading;
    },
    setError: (state, action) => {
      const { dataType, error } = action.payload;
      state.error[dataType] = error;
      state.isLoading[dataType] = false;
    },
    clearError: (state, action) => {
      const { dataType } = action.payload;
      state.error[dataType] = null;
    },
    setManagerStatus: (state, action) => {
      state.isManagerActive = action.payload;
    },
    resetData: (state, action) => {
      const { dataType } = action.payload;
      if (dataType) {
        state[dataType] = dataType === 'shifts' ? { drivers: {} } : [];
        state.lastUpdated[dataType] = null;
        state.error[dataType] = null;
      } else {
        // Reset all data
        return initialState;
      }
    }
  }
});

export const {
  setDrivers,
  setVehicles,
  setRides,
  setShifts,
  setCustomers,
  setActivities,
  setLoading,
  setError,
  clearError,
  setManagerStatus,
  resetData
} = appDataSlice.actions;

// Thunk für AppDataManager Start
export const startAppDataManager = () => async (dispatch) => {
  try {
    dispatch(setManagerStatus(false));
    
    // Callbacks für alle Datentypen registrieren
    const cleanupCallbacks = [
      appDataManager.registerCallback('drivers', (drivers) => {
        dispatch(setDrivers(drivers));
      }),
      appDataManager.registerCallback('vehicles', (vehicles) => {
        dispatch(setVehicles(vehicles));
      }),
      appDataManager.registerCallback('rides', (rides) => {
        dispatch(setRides(rides));
      }),
      appDataManager.registerCallback('shifts', (shifts) => {
        dispatch(setShifts(shifts));
      }),
      appDataManager.registerCallback('customers', (customers) => {
        dispatch(setCustomers(customers));
      }),
      appDataManager.registerCallback('activities', (activities) => {
        dispatch(setActivities(activities));
      })
    ];
    
    // Manager starten
    await appDataManager.start();
    
    dispatch(setManagerStatus(true));
    console.log('Redux: AppDataManager erfolgreich gestartet');
    
    // Cleanup-Funktion für später zurückgeben
    return () => {
      cleanupCallbacks.forEach(cleanup => cleanup());
    };
    
  } catch (error) {
    console.error('Redux: Fehler beim Starten des AppDataManager:', error);
    dispatch(setError({ dataType: 'drivers', error: error.message }));
    dispatch(setManagerStatus(false));
  }
};

// Thunk für AppDataManager Stop
export const stopAppDataManager = () => async (dispatch) => {
  try {
    await appDataManager.stop();
    dispatch(setManagerStatus(false));
    dispatch(resetData({})); // Reset all data
    
    console.log('Redux: AppDataManager gestoppt');
    
  } catch (error) {
    console.error('Redux: Fehler beim Stoppen des AppDataManager:', error);
    dispatch(setError({ dataType: 'drivers', error: error.message }));
  }
};

// Selektoren
export const selectDrivers = (state) => state.appData.drivers;
export const selectVehicles = (state) => state.appData.vehicles;
export const selectRides = (state) => state.appData.rides;
export const selectShifts = (state) => state.appData.shifts;
export const selectCustomers = (state) => state.appData.customers;

export const selectIsLoading = (dataType) => (state) => state.appData.isLoading[dataType];
export const selectError = (dataType) => (state) => state.appData.error[dataType];
export const selectLastUpdated = (dataType) => (state) => state.appData.lastUpdated[dataType];

export const selectIsManagerActive = (state) => state.appData.isManagerActive;
export const selectAllLoadingStates = (state) => state.appData.isLoading;
export const selectAllErrors = (state) => state.appData.error;

// Kombinierte Selektoren für Kompatibilität mit alten Slices
export const selectDriversState = (state) => ({
  drivers: state.appData.drivers,
  isLoading: state.appData.isLoading.drivers,
  error: state.appData.error.drivers
});

export const selectVehiclesState = (state) => ({
  vehicles: state.appData.vehicles,
  isLoading: state.appData.isLoading.vehicles,
  error: state.appData.error.vehicles
});

export const selectRidesState = (state) => ({
  rides: state.appData.rides,
  isLoading: state.appData.isLoading.rides,
  error: state.appData.error.rides
});

export const selectShiftsState = (state) => ({
  shifts: state.appData.shifts,
  data: state.appData.shifts, // Alias für ShiftCalendar Kompatibilität
  isLoading: state.appData.isLoading.shifts,
  error: state.appData.error.shifts
});

export const selectCustomersState = (state) => ({
  customers: state.appData.customers,
  isLoading: state.appData.isLoading.customers,
  error: state.appData.error.customers
});

export default appDataSlice.reducer;